import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTotalCountAction,
  fetchTotalRevenueAction,
  fetchTopUsersBySpendingAction,
  fetchTopUsersByPackagesAction,
  fetchTopDriversAction,
  fetchStatusDistributionAction,
  fetchDataByDateAction,
  fetchRecentTransactionsAction
} from "state/actions/analyticsActions";
import {
  Flex,
  Grid,
  useColorModeValue,
  Icon,
  Box
} from "@chakra-ui/react";
import {
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";
import PackageFlows from "./components/PackageFlows";
import Transactions from "../Billing/components/Transactions";
import { formatDate } from "utils/formatDate";
import Statistics from "../Components/Statistics";

export default function Dashboard() {
  const { t } = useTranslation();
  const iconBoxInside = useColorModeValue("white", "white");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  // Redux state mappings
  const packagesCount = useSelector(state => state.analytics.counts.packages);
  const totalRevenue = useSelector(state => state.analytics.totalRevenue);
  const topUsersBySpending = useSelector(state => state.analytics.topUsersBySpending);
  const topUsersByPackages = useSelector(state => state.analytics.topUsersByPackages);
  const dataByDate = useSelector(state => state.analytics.dataByDate);
  const recentTransactions = useSelector(state => state.analytics.recentTransactions);
  const getDirectionIcon = (current, previous) => {
    if (previous == null) return null;

    return current > previous
      ? <Icon as={FaArrowUp} color="green.400" boxSize={4} />
      : <Icon as={FaArrowDown} color="red.400" boxSize={4} />;
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // Dispatch actions to fetch data with correct table names
    dispatch(fetchTotalCountAction("users"));
    dispatch(fetchTotalCountAction("vehicles"));
    dispatch(fetchTotalCountAction("transactions"));
    dispatch(fetchTotalCountAction("packages"));
    dispatch(fetchTotalRevenueAction("transactions"));
    dispatch(fetchTopUsersBySpendingAction("transactions"));
    dispatch(fetchTopUsersByPackagesAction("packages"));
    dispatch(fetchStatusDistributionAction("deliveries"));
    dispatch(fetchDataByDateAction("users"));
    dispatch(fetchDataByDateAction("vehicles"));
    dispatch(fetchDataByDateAction("transactions"));
    dispatch(fetchDataByDateAction("packages"));
    dispatch(fetchTopDriversAction(100));
    dispatch(fetchRecentTransactionsAction(100));
  }, [dispatch]);


  // NEWEST
  let previousAmount = null;
  const newest = recentTransactions?.slice(0, 3).map((transaction, index) => {
    const icon = index === 0 ? null : getDirectionIcon(transaction.amount, previousAmount);
    previousAmount = transaction.amount;

    return {
      name: transaction.username || 'Unknown',
      logo: icon,
      date: formatDate(transaction.created_at),
      price: `P${transaction.amount.toFixed(2)}`
    };
  });

  // OLDER
  let previousOldAmount = null;
  const older = recentTransactions?.slice(-3).map((transaction, index) => {
    const icon = index === 0 ? null : getDirectionIcon(transaction.amount, previousOldAmount);
    previousOldAmount = transaction.amount;

    return {
      name: transaction.username || 'Unknown',
      logo: icon,
      date: formatDate(transaction.created_at),
      price: `P${transaction.amount.toFixed(2)}`,
    };
  });

  return (
    <Flex flexDirection='column' pt={{ base: "120px", md: "75px" }}>
       <Statistics />

      <Grid
        templateColumns={{ sm: "1fr", md: "1fr 1fr", lg: "2fr 1fr" }}
        templateRows={{ sm: "auto auto auto", md: "auto auto", lg: "auto auto" }}
        gap={{ base: "12px", md: "18px", lg: "24px" }}
      >
        {/* Left Column - Stacked Tables */}
        <Flex
          gridColumn={{ md: "1", lg: "1" }}
          direction="column"
          gap={{ base: "8px", md: "12px" }} 
        >
          <PackageFlows
            title={t('Top 5 Users by Packages')}
            amount={packagesCount || 0}
            captions={[
              t('Rank'),
              t('Username'),
              t('Packages Created'),
              t('Last Package Date'),
              t('Last Package Status')
            ]}
            data={
              topUsersByPackages?.slice(0, 5).map((user, index) => ({
                rank: index + 1,
                username: user.username || 'Unknown',
                packages: user.packages_created || 0,
                lastDate: formatDate(user.last_package_date) || 'N/A',
                status: user.status
              })) || []
            }
            isLoading={isLoading}
          />

          <PackageFlows
            variant="spending"
            title={t('Top 5 Users by Spending')}
            amount={totalRevenue || 0}
            captions={[
              t('Rank'),
              t('Username'),
              t('Total Spent'),
              t('Last Spend Date'),
            ]}
            data={
              topUsersBySpending?.slice(0, 5).map((user, index) => ({
                rank: index + 1,
                username: user.username || 'Unknown',
                totalSpend: user.total_spent || 0,
                lastSpendDate: formatDate(user.last_spend_date) || 'N/A',
              })) || []
            }
            isLoading={isLoading}
          />
        </Flex>

        {/* Right Column - Transactions */}
        <Box
          gridColumn={{ md: "2", lg: "2" }}
          gridRow={{ md: "1 / span 2", lg: "1 / span 2" }}
        >
          <Transactions
            title="Recent Transactions"
            date="23 - 30 March"
            newestTransactions={newest}
            olderTransactions={older}
            isLoading={isLoading}
          />
        </Box>
      </Grid>
    </Flex>
  );
}
